import { useState, useRef, useEffect, useCallback } from 'react';
import { ZoomIn, ZoomOut, Move, Download, Layers, Maximize2 } from 'lucide-react';
import { usePatternStore } from '../stores/patternStore';
import type { PatternPiece } from '../types';

interface ViewportState {
  x: number;
  y: number;
  zoom: number;
}

interface CanvasState {
  width: number;
  height: number;
  bounds: { minX: number; minY: number; maxX: number; maxY: number };
}

export function PatternViewer() {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [canvasState, setCanvasState] = useState<CanvasState>({
    width: 1000,
    height: 600,
    bounds: { minX: 0, minY: 0, maxX: 1000, maxY: 600 },
  });

  const { pieces, selectedPiece, setSelectedPiece, loadDemoPattern } = usePatternStore();
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load demo pattern on mount if no pieces
  useEffect(() => {
    if (pieces.length === 0) {
      loadDemoPattern();
    }
  }, []);

  // Calculate bounds from all pieces
  useEffect(() => {
    if (pieces.length === 0) {
      setCanvasState({
        width: 1000,
        height: 600,
        bounds: { minX: 0, minY: 0, maxX: 1000, maxY: 600 },
      });
      return;
    }

    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    pieces.forEach((piece) => {
      const bounds = extractPathBounds(piece.path);
      minX = Math.min(minX, bounds.minX);
      minY = Math.min(minY, bounds.minY);
      maxX = Math.max(maxX, bounds.maxX);
      maxY = Math.max(maxY, bounds.maxY);
    });

    // Add padding
    const padding = 50;
    minX -= padding;
    minY -= padding;
    maxX += padding;
    maxY += padding;

    const width = Math.max(maxX - minX, 400);
    const height = Math.max(maxY - minY, 300);

    setCanvasState({
      width,
      height,
      bounds: { minX, minY, maxX, maxY },
    });
  }, [pieces]);

  // Extract bounding box from SVG path string
  const extractPathBounds = (pathStr: string) => {
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    // Simple regex to extract numbers from path
    const numbers = pathStr.match(/-?\d+\.?\d*/g);
    if (numbers) {
      for (let i = 0; i < numbers.length; i += 2) {
        const x = parseFloat(numbers[i]);
        const y = parseFloat(numbers[i + 1]);
        if (!isNaN(x) && !isNaN(y)) {
          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x);
          maxY = Math.max(maxY, y);
        }
      }
    }

    return { minX, minY, maxX, maxY };
  };

  // Zoom handlers
  const handleZoomIn = useCallback(() => {
    setZoom((z) => Math.min(z * 1.2, 4));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((z) => Math.max(z / 1.2, 0.3));
  }, []);

  const handleResetZoom = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  // Pan handlers
  const handleMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    if (e.button !== 1) return; // Middle mouse button
    setIsDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!isDragging) return;
    setPan({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Wheel zoom
  const handleWheel = (e: React.WheelEvent<SVGSVGElement>) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom((z) => Math.max(0.3, Math.min(z * delta, 4)));
  };

  // Piece click
  const handlePieceClick = (e: React.MouseEvent, pieceId: string) => {
    e.stopPropagation();
    setSelectedPiece(selectedPiece === pieceId ? null : pieceId);
  };

  // Export handlers
  const handleExportSVG = () => {
    if (!svgRef.current) return;
    const svg = svgRef.current.cloneNode(true) as SVGSVGElement;
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svg);
    downloadFile(svgString, 'pattern.svg', 'image/svg+xml');
  };

  const handleExportDXF = () => {
    // Generate simple DXF from pieces
    let dxf = generateSimpleDXF(pieces);
    downloadFile(dxf, 'pattern.dxf', 'application/dxf');
  };

  const downloadFile = (content: string, filename: string, type: string) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const generateSimpleDXF = (pieces: PatternPiece[]): string => {
    let dxf = `  0\nSECTION\n  2\nHEADER\n  9\n$ACADVER\n  1\nAC1021\n  0\nENDSEC\n  0\nSECTION\n  2\nENTITIES\n`;

    pieces.forEach((piece, idx) => {
      // Add polyline for each piece
      const numbers = piece.path.match(/-?\d+\.?\d*/g) || [];
      const points: [number, number][] = [];

      for (let i = 0; i < numbers.length; i += 2) {
        points.push([parseFloat(numbers[i]), parseFloat(numbers[i + 1])]);
      }

      if (points.length > 0) {
        dxf += `  0\nLWPOLYLINE\n  8\nPiece${idx}\n  70\n1\n`;
        points.forEach(([x, y]) => {
          dxf += `  0\nVERTEX\n  8\nPiece${idx}\n 10\n${x}\n 20\n${y}\n`;
        });
        dxf += `  0\nSEQEND\n`;
      }
    });

    dxf += `  0\nENDSEC\n  0\nEOF\n`;
    return dxf;
  };

  const viewBox = `${canvasState.bounds.minX} ${canvasState.bounds.minY} ${canvasState.bounds.maxX - canvasState.bounds.minX} ${canvasState.bounds.maxY - canvasState.bounds.minY}`;

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-zinc-950 to-zinc-900">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 bg-zinc-900/50">
        <div className="flex items-center gap-1">
          <button
            onClick={handleZoomOut}
            className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
            title="Zoom Out (Scroll Down)"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="text-xs text-zinc-500 w-12 text-center font-mono">
            {Math.round(zoom * 100)}%
          </span>
          <button
            onClick={handleZoomIn}
            className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
            title="Zoom In (Scroll Up)"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          <div className="w-px h-6 bg-zinc-700 mx-1" />
          <button
            onClick={handleResetZoom}
            className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
            title="Reset View"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
          <button
            className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors cursor-move"
            title="Middle-click to pan"
          >
            <Move className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportSVG}
            disabled={pieces.length === 0}
            className="px-3 py-2 text-xs bg-zinc-800 text-zinc-300 hover:bg-zinc-700 hover:text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Export SVG
          </button>
          <button
            onClick={handleExportDXF}
            disabled={pieces.length === 0}
            className="flex items-center gap-2 px-3 py-2 bg-progeny-600 hover:bg-progeny-500 disabled:bg-zinc-700 disabled:text-zinc-500 rounded-lg text-xs text-white transition-colors disabled:cursor-not-allowed"
          >
            <Download className="w-3 h-3" />
            Export DXF
          </button>
        </div>
      </div>

      {/* Canvas */}
      <div
        ref={containerRef}
        className="flex-1 overflow-hidden bg-zinc-900 relative"
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        {pieces.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-8">
            <Layers className="w-12 h-12 text-zinc-700 mb-3" />
            <p className="text-zinc-500 text-sm">
              Pattern pieces will appear here after generation
            </p>
            <p className="text-zinc-600 text-xs mt-2">
              Generate a pattern from chat to see interactive pieces
            </p>
          </div>
        ) : (
          <svg
            ref={svgRef}
            className="w-full h-full transition-transform duration-75"
            viewBox={viewBox}
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transformOrigin: '0 0',
              cursor: 'grab',
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onWheel={handleWheel}
          >
            {/* Define markers */}
            <defs>
              {/* Grid pattern */}
              <pattern id="smallGrid" width="10" height="10" patternUnits="userSpaceOnUse">
                <path d="M 10 0 L 0 0 0 10" fill="none" stroke="#27272a" strokeWidth="0.5" />
              </pattern>
              <pattern id="grid" width="100" height="100" patternUnits="userSpaceOnUse">
                <rect width="100" height="100" fill="url(#smallGrid)" />
                <path d="M 100 0 L 0 0 0 100" fill="none" stroke="#3f3f46" strokeWidth="1" />
              </pattern>

              {/* Arrow marker for grainlines */}
              <marker id="arrowGrain" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" markerUnits="strokeWidth">
                <path d="M0,0 L0,6 L9,3 z" fill="#60a5fa" />
              </marker>
              <marker id="arrowGrainSelected" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" markerUnits="strokeWidth">
                <path d="M0,0 L0,6 L9,3 z" fill="#fbbf24" />
              </marker>
            </defs>

            {/* Grid background */}
            <rect
              x={canvasState.bounds.minX}
              y={canvasState.bounds.minY}
              width={canvasState.bounds.maxX - canvasState.bounds.minX}
              height={canvasState.bounds.maxY - canvasState.bounds.minY}
              fill="url(#grid)"
            />

            {/* Ruler - horizontal */}
            <g className="ruler">
              <line
                x1={canvasState.bounds.minX}
                y1={canvasState.bounds.minY}
                x2={canvasState.bounds.maxX}
                y2={canvasState.bounds.minY}
                stroke="#52525b"
                strokeWidth="1"
              />
              {generateRulerTicks(canvasState.bounds.minX, canvasState.bounds.maxX, true).map((tick, idx) => (
                <g key={`hruler-${idx}`}>
                  <line x1={tick.pos} y1={canvasState.bounds.minY} x2={tick.pos} y2={canvasState.bounds.minY - 5} stroke="#71717a" strokeWidth="1" />
                  {tick.label && (
                    <text x={tick.pos} y={canvasState.bounds.minY - 10} fontSize="8" fill="#71717a" textAnchor="middle">
                      {tick.label}
                    </text>
                  )}
                </g>
              ))}
            </g>

            {/* Ruler - vertical */}
            <g className="ruler">
              <line
                x1={canvasState.bounds.minX}
                y1={canvasState.bounds.minY}
                x2={canvasState.bounds.minX}
                y2={canvasState.bounds.maxY}
                stroke="#52525b"
                strokeWidth="1"
              />
              {generateRulerTicks(canvasState.bounds.minY, canvasState.bounds.maxY, false).map((tick, idx) => (
                <g key={`vruler-${idx}`}>
                  <line x1={canvasState.bounds.minX} y1={tick.pos} x2={canvasState.bounds.minX - 5} y2={tick.pos} stroke="#71717a" strokeWidth="1" />
                  {tick.label && (
                    <text x={canvasState.bounds.minX - 10} y={tick.pos + 3} fontSize="8" fill="#71717a" textAnchor="end">
                      {tick.label}
                    </text>
                  )}
                </g>
              ))}
            </g>

            {/* Pattern pieces */}
            {pieces.map((piece) => {
              const isSelected = selectedPiece === piece.id;
              return (
                <g
                  key={piece.id}
                  onClick={(e) => handlePieceClick(e, piece.id)}
                  className="pattern-piece cursor-pointer transition-all"
                >
                  {/* Main piece outline */}
                  <path
                    d={piece.path}
                    fill={isSelected ? '#0c8ee625' : '#1a1a1e40'}
                    stroke={isSelected ? '#0c8ee6' : '#52525b'}
                    strokeWidth={isSelected ? '2' : '1.5'}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />

                  {/* Seam allowance (if specified) */}
                  {piece.seamAllowance && piece.seamAllowance > 0 && (
                    <path
                      d={piece.path}
                      fill="none"
                      stroke="#71717a"
                      strokeWidth="1"
                      strokeDasharray="4,4"
                      opacity="0.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  )}

                  {/* Grainline */}
                  {piece.grainline && (
                    <>
                      <line
                        x1={piece.grainline.x1}
                        y1={piece.grainline.y1}
                        x2={piece.grainline.x2}
                        y2={piece.grainline.y2}
                        stroke={isSelected ? '#fbbf24' : '#60a5fa'}
                        strokeWidth="1.5"
                        markerEnd={isSelected ? 'url(#arrowGrainSelected)' : 'url(#arrowGrain)'}
                      />
                      {/* Grainline label */}
                      <text
                        x={(piece.grainline.x1 + piece.grainline.x2) / 2 + 8}
                        y={(piece.grainline.y1 + piece.grainline.y2) / 2}
                        fontSize="7"
                        fill="#60a5fa"
                        opacity="0.7"
                      >
                        ↑ Grain
                      </text>
                    </>
                  )}

                  {/* Notches */}
                  {piece.notches &&
                    piece.notches.map((notch, idx) => (
                      <g key={`notch-${idx}`}>
                        <line
                          x1={notch.x - 3 * Math.cos(notch.angle)}
                          y1={notch.y - 3 * Math.sin(notch.angle)}
                          x2={notch.x + 3 * Math.cos(notch.angle)}
                          y2={notch.y + 3 * Math.sin(notch.angle)}
                          stroke="#ef4444"
                          strokeWidth="1"
                        />
                      </g>
                    ))}

                  {/* Piece label */}
                  <text
                    x={piece.labelPos.x}
                    y={piece.labelPos.y}
                    fill={isSelected ? '#0c8ee6' : '#a1a1aa'}
                    fontSize={isSelected ? '11' : '10'}
                    fontWeight={isSelected ? 'bold' : 'normal'}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    pointerEvents="none"
                    className="select-none"
                  >
                    {piece.name}
                  </text>
                </g>
              );
            })}

            {/* Scale indicator */}
            <g className="scale-indicator" opacity="0.6">
              <line
                x1={canvasState.bounds.maxX - 80}
                y1={canvasState.bounds.maxY - 30}
                x2={canvasState.bounds.maxX - 30}
                y2={canvasState.bounds.maxY - 30}
                stroke="#71717a"
                strokeWidth="1"
              />
              <text
                x={(canvasState.bounds.maxX - 80 + canvasState.bounds.maxX - 30) / 2}
                y={canvasState.bounds.maxY - 15}
                fontSize="8"
                fill="#71717a"
                textAnchor="middle"
              >
                10cm
              </text>
            </g>
          </svg>
        )}
      </div>

      {/* Piece list */}
      {pieces.length > 0 && (
        <div className="border-t border-zinc-800 p-3 bg-zinc-900/50 max-h-24 overflow-y-auto">
          <h4 className="text-xs font-medium text-zinc-400 mb-2">
            Pieces ({pieces.length}) — Click to select
          </h4>
          <div className="flex flex-wrap gap-2">
            {pieces.map((piece) => (
              <button
                key={piece.id}
                onClick={() => setSelectedPiece(selectedPiece === piece.id ? null : piece.id)}
                className={`px-2 py-1 rounded text-xs transition-all ${
                  selectedPiece === piece.id
                    ? 'bg-progeny-600/30 text-progeny-300 border border-progeny-400/50 shadow-lg'
                    : 'bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700'
                }`}
              >
                {piece.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Generate ruler tick marks
 */
function generateRulerTicks(start: number, end: number, horizontal: boolean) {
  const ticks = [];
  const range = end - start;
  const majorInterval = 100; // Every 100 units
  const minorInterval = 20; // Every 20 units

  for (let i = start; i <= end; i += minorInterval) {
    const isMajor = (i - start) % majorInterval === 0;
    ticks.push({
      pos: i,
      label: isMajor ? `${((i - start) / 100).toFixed(0)}cm` : '',
    });
  }

  return ticks;
}
